/**
 * TaskTypesStep — Step 2 of the Job Management Wizard
 *
 * Simplified: name + attached forms via task_form table with sequence ordering.
 */

import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import type { TaskTypeItem } from '@skewb-works/contracts';
import {
  ArrowDown,
  ArrowUp,
  FileText,
  GripVertical,
  Pencil,
  Plus,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useFormList } from '../hooks/useFormBuilder';
import {
  useCreateTaskType,
  useDeleteTaskType,
  useSetTaskForms,
  useTaskForms,
  useTaskTypes,
  useUpdateTaskType,
} from '../hooks/useJobManagement';

interface SelectedForm {
  formId: number;
  formName: string;
}

export default function TaskTypesStep() {
  const { data, isLoading } = useTaskTypes();
  const createMutation = useCreateTaskType();
  const updateMutation = useUpdateTaskType();
  const deleteMutation = useDeleteTaskType();
  const setTaskFormsMutation = useSetTaskForms();

  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [selectedForms, setSelectedForms] = useState<SelectedForm[]>([]);
  const [formSearch, setFormSearch] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  const taskTypes = data?.data ?? [];
  const { data: formListData } = useFormList();
  const allForms = formListData?.values ?? [];
  const { data: taskFormsData } = useTaskForms(editingId);

  // When editing, load existing forms from task_form table
  useEffect(() => {
    if (editingId && taskFormsData?.data) {
      setSelectedForms(
        taskFormsData.data.map((tf) => ({
          formId: tf.formId,
          formName: tf.formName ?? `Form #${tf.formId}`,
        })),
      );
    }
  }, [editingId, taskFormsData]);

  const filtered = useMemo(() => {
    if (!search) return taskTypes;
    const q = search.toLowerCase();
    return taskTypes.filter((t) => t.name?.toLowerCase().includes(q));
  }, [taskTypes, search]);

  const availableForms = useMemo(() => {
    const selectedIds = new Set(selectedForms.map((f) => f.formId));
    let pool = allForms.filter((f: any) => !selectedIds.has(f.id));
    if (formSearch) {
      const q = formSearch.toLowerCase();
      pool = pool.filter((f: any) => f.name?.toLowerCase().includes(q));
    }
    return pool;
  }, [allForms, selectedForms, formSearch]);

  function openCreate() {
    setEditingId(null);
    setName('');
    setSelectedForms([]);
    setFormSearch('');
    setDialogOpen(true);
  }

  function openEdit(tt: TaskTypeItem) {
    setEditingId(tt.id);
    setName(tt.name ?? '');
    setSelectedForms([]);
    setFormSearch('');
    setDialogOpen(true);
  }

  function addForm(formId: number, formName: string) {
    if (selectedForms.some((f) => f.formId === formId)) return;
    setSelectedForms((prev) => [...prev, { formId, formName }]);
  }

  function removeForm(formId: number) {
    setSelectedForms((prev) => prev.filter((f) => f.formId !== formId));
  }

  function moveForm(index: number, direction: -1 | 1) {
    setSelectedForms((prev) => {
      const list = [...prev];
      const target = index + direction;
      if (target < 0 || target >= list.length) return prev;
      [list[index], list[target]] = [list[target], list[index]];
      return list;
    });
  }

  async function handleSave() {
    let taskTypeId = editingId;

    if (editingId) {
      await updateMutation.mutateAsync({ id: editingId, body: { name } });
    } else {
      const result = await createMutation.mutateAsync({ name });
      taskTypeId = result.id;
    }

    if (taskTypeId) {
      await setTaskFormsMutation.mutateAsync({
        taskTypeId,
        forms: selectedForms.map((f, idx) => ({
          formId: f.formId,
          sequence: idx + 1,
        })),
      });
    }

    setDialogOpen(false);
  }

  async function handleDelete(id: number) {
    await deleteMutation.mutateAsync(id);
    setDeleteConfirmId(null);
  }

  const isSaving =
    createMutation.isPending || updateMutation.isPending || setTaskFormsMutation.isPending;

  // Build a map of taskTypeId → form names for the table display
  const taskFormDisplayMap = useMemo(() => {
    const map = new Map<number, string[]>();
    for (const tt of taskTypes) {
      if (tt.formAssignment) {
        map.set(
          tt.id,
          tt.formAssignment
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean),
        );
      }
    }
    return map;
  }, [taskTypes]);

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search task types..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button onClick={openCreate} style={{ backgroundColor: '#E84A2B' }} className="text-white">
          <Plus className="w-4 h-4 mr-2" />
          Add Task Type
        </Button>
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Assigned Forms</TableHead>
              <TableHead className="text-center w-[80px]">System</TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                  Loading...
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                  {search ? 'No task types match your search' : 'No task types found'}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((tt) => {
                const formNames = taskFormDisplayMap.get(tt.id) ?? [];
                return (
                  <TableRow key={tt.id}>
                    <TableCell className="font-medium">{tt.name ?? '-'}</TableCell>
                    <TableCell>
                      {formNames.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {formNames.map((formName, idx) => (
                            <Badge key={formName} variant="outline" className="text-xs gap-1">
                              <span className="text-muted-foreground">{idx + 1}.</span>
                              <FileText className="w-3 h-3" />
                              {formName}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">No forms</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {tt.isSystem ? (
                        <Badge variant="secondary" className="text-xs">
                          System
                        </Badge>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(tt)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteConfirmId(tt.id)}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Task Type' : 'Add Task Type'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="tt-name">
                Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="tt-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter task type name"
              />
            </div>

            {/* Attached Forms (ordered) */}
            <div className="space-y-2">
              <Label>
                Attached Forms <span className="text-red-500">*</span>
              </Label>
              <p className="text-xs text-muted-foreground">
                Forms are presented to the engineer in the order shown below.
              </p>

              {/* Ordered list */}
              {selectedForms.length > 0 && (
                <div className="border rounded-lg divide-y">
                  {selectedForms.map((sf, idx) => (
                    <div key={sf.formId} className="flex items-center gap-2 px-3 py-2">
                      <GripVertical className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <span className="text-xs font-medium text-muted-foreground w-5">
                        {idx + 1}.
                      </span>
                      <FileText className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                      <span className="flex-1 text-sm text-foreground">{sf.formName}</span>
                      <div className="flex items-center gap-0.5">
                        <button
                          type="button"
                          onClick={() => moveForm(idx, -1)}
                          disabled={idx === 0}
                          className="p-1 rounded hover:bg-muted disabled:opacity-30"
                          title="Move up"
                        >
                          <ArrowUp className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveForm(idx, 1)}
                          disabled={idx === selectedForms.length - 1}
                          className="p-1 rounded hover:bg-muted disabled:opacity-30"
                          title="Move down"
                        >
                          <ArrowDown className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeForm(sf.formId)}
                          className="p-1 rounded hover:bg-red-50 text-muted-foreground hover:text-destructive"
                          title="Remove"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add form */}
              <div className="border rounded-lg overflow-hidden">
                <div className="p-2 border-b bg-muted/30">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                    <Input
                      placeholder="Search and add forms..."
                      value={formSearch}
                      onChange={(e) => setFormSearch(e.target.value)}
                      className="pl-8 h-8 text-sm"
                    />
                  </div>
                </div>
                <div className="max-h-[150px] overflow-y-auto">
                  {availableForms.length === 0 ? (
                    <div className="p-3 text-sm text-muted-foreground text-center">
                      {selectedForms.length > 0 && !formSearch
                        ? 'All forms have been added'
                        : formSearch
                          ? 'No forms match your search'
                          : 'No forms available'}
                    </div>
                  ) : (
                    availableForms.map((f: any) => (
                      <button
                        key={f.id}
                        type="button"
                        onClick={() => addForm(f.id, f.name)}
                        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-muted/50 text-left text-sm"
                      >
                        <Plus className="w-3.5 h-3.5 text-muted-foreground" />
                        <span className="text-foreground">{f.name}</span>
                      </button>
                    ))
                  )}
                </div>
              </div>

              {selectedForms.length === 0 && (
                <p className="text-xs text-red-500">At least one form must be attached</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!name.trim() || selectedForms.length === 0 || isSaving}
              style={{ backgroundColor: '#E84A2B' }}
              className="text-white"
            >
              {isSaving ? 'Saving...' : editingId ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirmId !== null} onOpenChange={() => setDeleteConfirmId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Task Type</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete this task type? This action can be reversed by an
            administrator.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
